import React, { useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { buildItemPriceTimeline } from '../utils/itemPriceTimeline.mjs';

const formatShortDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatSignedMoney = (value) => {
  if (!Number.isFinite(value)) return '—';
  if (value === 0) return '$0.00';
  return `${value > 0 ? '+' : '−'}$${Math.abs(value).toFixed(2)}`;
};

const buildSparkPath = (events, width, height) => {
  const priced = events.filter((event) => Number.isFinite(event.price));
  if (!priced.length) return { path: '', points: [] };
  const times = priced.map((event) => new Date(event.timestamp).getTime());
  const prices = priced.map((event) => event.price);
  const minTime = Math.min(...times);
  const maxTime = Math.max(...times);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const pad = 10;
  const xFor = (time, index) => maxTime === minTime
    ? pad + ((width - (pad * 2)) * (priced.length === 1 ? 0.5 : index / (priced.length - 1)))
    : pad + (((time - minTime) / (maxTime - minTime)) * (width - (pad * 2)));
  const yFor = (price) => maxPrice === minPrice
    ? height / 2
    : pad + (((maxPrice - price) / (maxPrice - minPrice)) * (height - (pad * 2)));
  const points = priced.map((event, index) => ({
    event,
    x: xFor(times[index], index),
    y: yFor(event.price),
  }));
  const path = points.reduce((value, point, index) => {
    if (index === 0) return `M ${point.x} ${point.y}`;
    const previous = points[index - 1];
    return `${value} L ${point.x} ${previous.y} L ${point.x} ${point.y}`;
  }, '');
  return { path, points };
};

export default function PriceChangeHistoryHover({ row }) {
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const timeline = useMemo(
    () => buildItemPriceTimeline(row.item, row.historyRows, row.basis),
    [row.item, row.historyRows, row.basis],
  );
  const chart = useMemo(() => buildSparkPath(timeline.events, 320, 92), [timeline.events]);

  const rect = open ? triggerRef.current?.getBoundingClientRect() : null;
  const left = rect ? Math.max(12, Math.min(rect.left, window.innerWidth - 372)) : 12;
  const top = rect
    ? (rect.top > 290 ? rect.top - 272 : Math.min(window.innerHeight - 272, rect.bottom + 8))
    : 12;
  const recentEvents = timeline.events.slice(-3).reverse();

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen(true)}
        aria-expanded={open}
        aria-label={`${row.itemName}. Show price history.`}
        style={{
          appearance: 'none',
          background: 'none',
          border: 0,
          borderBottom: '1px dotted #7E9A6C',
          color: '#3D2518',
          cursor: 'pointer',
          font: 'inherit',
          fontWeight: 700,
          lineHeight: 1.35,
          padding: 0,
          textAlign: 'left',
          whiteSpace: 'normal',
        }}
      >
        {row.itemName} <span aria-hidden="true" style={{ color: '#6F8F5B', fontSize: '0.72rem' }}>↗</span>
      </button>

      {open && rect && ReactDOM.createPortal(
        <div
          role="tooltip"
          style={{
            position: 'fixed',
            left,
            top,
            width: 360,
            zIndex: 10050,
            background: '#FFFEFC',
            border: '1px solid #DCCFC4',
            borderRadius: 14,
            boxShadow: '0 16px 42px rgba(58,46,40,0.22)',
            padding: '0.85rem',
            pointerEvents: 'none',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'baseline' }}>
            <div>
              <div style={{ color: '#3A2E28', fontSize: '0.92rem', fontWeight: 800 }}>{row.itemName}</div>
              <div style={{ color: '#7B6A61', fontSize: '0.72rem', marginTop: '0.1rem' }}>
                {row.vendor || 'No vendor'} · {timeline.basis === 'case' ? 'Case price' : 'Unit price'}
              </div>
            </div>
            <div style={{ color: row.absChange >= 0 ? '#39702D' : '#A74435', fontSize: '0.9rem', fontWeight: 800, whiteSpace: 'nowrap' }}>
              {formatSignedMoney(row.absChange)}
            </div>
          </div>

          <svg viewBox="0 0 320 92" width="100%" height="92" aria-hidden="true" style={{ display: 'block', marginTop: '0.55rem', background: '#F7F8F4', borderRadius: 9 }}>
            <line x1="10" y1="46" x2="310" y2="46" stroke="#E0E7DA" strokeDasharray="3 4" />
            {chart.path && <path d={chart.path} fill="none" stroke="#5E7F49" strokeWidth="3" strokeLinejoin="round" />}
            {chart.points.map((point) => (
              <circle key={point.event.id} cx={point.x} cy={point.y} r="4" fill="#FFFEFC" stroke="#4F713C" strokeWidth="2.5" />
            ))}
          </svg>

          <div style={{ display: 'grid', gap: '0.35rem', marginTop: '0.6rem' }}>
            {recentEvents.map((event) => (
              <div key={event.id} style={{ display: 'grid', gridTemplateColumns: '82px 1fr', gap: '0.45rem', fontSize: '0.72rem' }}>
                <span style={{ color: '#8A7468' }}>{formatShortDate(event.timestamp)}</span>
                <span style={{ color: '#4E433D' }}><strong>{event.title}</strong> · {event.description}</span>
              </div>
            ))}
          </div>
          <div style={{ color: '#8A7468', fontSize: '0.67rem', marginTop: '0.6rem' }}>
            Showing {timeline.events.length} recorded event{timeline.events.length === 1 ? '' : 's'} from item history.
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
